import React, { useState, useEffect } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import apiService from '../services/api';

const Settings = () => {
  const [formData, setFormData] = useState({
    default_symbol: '',
    default_strategy: '',
    default_start_date: new Date(new Date().setFullYear(new Date().getFullYear() - 1)),
    default_end_date: new Date(),
  });
  
  const [strategies, setStrategies] = useState([]);
  const [symbols, setSymbols] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);
  
  // Fetch strategies, symbols, and user settings on mount
  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        setLoading(true);
        
        // Fetch strategies
        const strategiesResponse = await apiService.backtest.getStrategies();
        setStrategies(strategiesResponse.data);
        
        // Fetch symbols
        const symbolsResponse = await apiService.common.getSymbols();
        setSymbols(symbolsResponse.data);
        
        // Fetch user settings
        const settingsResponse = await apiService.settings.get();
        const settings = settingsResponse.data;
        
        // Apply settings to form
        setFormData({
          default_symbol: settings.default_symbol || '',
          default_strategy: settings.default_strategy || '',
          default_start_date: settings.default_start_date ? new Date(settings.default_start_date) : new Date(new Date().setFullYear(new Date().getFullYear() - 1)),
          default_end_date: settings.default_end_date ? new Date(settings.default_end_date) : new Date(),
        });
        
        setError(null);
      } catch (err) {
        console.error('Error fetching initial data:', err);
        setError('Failed to load settings. Please try again later.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchInitialData();
  }, []);
  
  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };
  
  // Handle date changes
  const handleDateChange = (date, name) => {
    setFormData(prev => ({ ...prev, [name]: date }));
  };
  
  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setSaving(true);
      setSuccess(false);
      setError(null);
      
      // Format dates for API
      const formattedData = {
        ...formData,
        default_start_date: formData.default_start_date.toISOString().split('T')[0],
        default_end_date: formData.default_end_date.toISOString().split('T')[0],
      };
      
      // Update settings
      await apiService.settings.update(formattedData);
      
      setSuccess(true);
    } catch (err) {
      console.error('Error saving settings:', err);
      setError('Failed to save settings. Please try again later.');
      setSuccess(false);
    } finally {
      setSaving(false);
    }
  };
  
  return (
    <div className="bg-white shadow overflow-hidden sm:rounded-lg">
      <div className="px-4 py-5 border-b border-gray-200 sm:px-6">
        <h3 className="text-lg leading-6 font-medium text-gray-900">
          Settings
        </h3>
        <p className="mt-1 max-w-2xl text-sm text-gray-500">
          Manage your application preferences.
        </p>
      </div>
      
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 m-4 rounded relative" role="alert">
          <span className="block sm:inline">{error}</span>
        </div>
      )}
      
      {success && (
        <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 m-4 rounded relative" role="alert">
          <span className="block sm:inline">Settings saved successfully!</span>
        </div>
      )}
      
      {loading ? (
        <div className="px-4 py-5 sm:p-6 text-center">
          <p className="text-gray-500">Loading settings...</p>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="px-4 py-5 sm:p-6">
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            {/* Default Symbol */}
            <div>
              <label htmlFor="default_symbol" className="block text-sm font-medium text-gray-700">
                Default Symbol
              </label>
              <select
                id="default_symbol"
                name="default_symbol"
                value={formData.default_symbol}
                onChange={handleInputChange}
                className="mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                disabled={saving}
              >
                <option value="">None</option>
                {symbols.map(symbol => (
                  <option key={symbol.id} value={symbol.id}>
                    {symbol.name}
                  </option>
                ))}
              </select>
            </div>
            
            {/* Default Strategy */}
            <div>
              <label htmlFor="default_strategy" className="block text-sm font-medium text-gray-700">
                Default Strategy
              </label>
              <select
                id="default_strategy"
                name="default_strategy"
                value={formData.default_strategy}
                onChange={handleInputChange}
                className="mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                disabled={saving}
              >
                <option value="">None</option>
                {strategies.map(strategy => (
                  <option key={strategy.id} value={strategy.id}>
                    {strategy.name}
                  </option>
                ))}
              </select>
            </div>
            
            {/* Default Date Range */}
            <div>
              <label htmlFor="default_start_date" className="block text-sm font-medium text-gray-700">
                Default Start Date
              </label>
              <DatePicker
                id="default_start_date"
                selected={formData.default_start_date}
                onChange={(date) => handleDateChange(date, 'default_start_date')}
                className="mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                dateFormat="yyyy-MM-dd"
                maxDate={formData.default_end_date}
                disabled={saving}
              />
            </div>
            
            <div>
              <label htmlFor="default_end_date" className="block text-sm font-medium text-gray-700">
                Default End Date
              </label>
              <DatePicker
                id="default_end_date"
                selected={formData.default_end_date}
                onChange={(date) => handleDateChange(date, 'default_end_date')}
                className="mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                dateFormat="yyyy-MM-dd"
                minDate={formData.default_start_date}
                maxDate={new Date()}
                disabled={saving}
              />
            </div>
          </div>
          
          {/* Save Button */}
          <div className="mt-6">
            <button
              type="submit"
              className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
              disabled={saving}
            >
              {saving ? 'Saving...' : 'Save Settings'}
            </button>
          </div>
        </form>
      )}
    </div>
  );
};

export default Settings;
